/** The SERVING OPEN (wire contract §1, §2, §5; option B): one POST names the
 *  islands a page shows, and the answer says where each one's state and
 *  enrichment live. The sync client reads it in `openServing`.
 *
 *  This module holds the request and the answer's types, nothing stateful. */

import type { StateManifest } from "../state-packs/loader";

export type OpenRequest = {
  /** `spec`: an expanded island's nested load spec, passed back on its own
   *  re-open; the server clamps it to the class's spec (S1f, VC-D) */
  islands: {
    type: string; id: string; branch?: string; at?: string; spec?: unknown;
    /** the newest draft segment this client holds for the lane: on the
     *  same gen AND segment epoch the open names only the ops after `to`
     *  (VC-D, v1.9e; the epoch: item 95, 2026-10-14) */
    last_segment?: { gen: string; epoch?: string; to: number };
    /** stale claims sent or left out; unset = the class defaults. The open
     *  writes the rule into both claims URLs (`&stale=`), so they are used
     *  as given (contract v1.9f §4) */
    claims?: { stale: "send" | "omit" };
  }[];
  channels?: ("state" | "computed" | "claims" | "unversioned")[];
};

export type OpenRefusal = {
  island: string;
  code: number; // 403 | 404 | 409
  detail?: string | { code?: string; retry_after_ms?: number; message?: string; reason?: string; organisation?: { id: string; name?: string } };
};

export type OpenIsland = {
  island: string;
  type: string;
  opened_by: string[];
  lane: string;
  line: string;
  head: string;
  /** `<base commit>.<lane_gen>` for a draft; null for a lane with no draft yet */
  gen: string | null;
  /** what the lane's segments are relative to (item 95, Tom 2026-10-14: no
   *  re-fetch of a draft where no re-base took place): a line's head, a
   *  draft's gen. The gen is the op sequence's identity and survives a seal;
   *  the epoch moves with it, so segments held from before a seal are never
   *  continued — they are inside the new base. */
  segment_epoch?: string | null;
  state: {
    /** null for an island born in the log (a task before its first commit):
     *  no base at all, its line's segment holds everything (v1.9) */
    base: string | null;
    manifest?: StateManifest;
    commits_after_base: string[];
    draft_segments: string[];
    tail: string;
  };
  computed?: { base: string; since: string };
  claims?: { base: string; since: string };
  unversioned?: string[];
  /** answered AT a holder's pin (VC-D's pinned open): that commit only —
   *  `lane` null, `gen` null, no tail or draft segments, and `computed`
   *  `{unavailable: "not_evaluated_at_this_version"}` (pins/pinned-islands) */
  at?: string | null;
  /** an expanded island's nested load spec, as it was opened (S1f) */
  spec?: unknown;
  /** on the requested holder's entry: its references reaching one island at
   *  different versions (VC-D; `data/pins/pin-conflicts`) */
  pin_conflicts?: unknown;
  /** on the requested holder's entry: its pins whose target was retired,
   *  `[{pin, island, type, name}]`, only islands this reader may read
   *  (VC-D R4; `data/pins/retired-pins`); absent when none */
  retired_pins?: unknown;
};

export type OpenAnswer = {
  cursor: number;
  cursor_kind: string;
  fs: string;
  refused: OpenRefusal[];
  islands: OpenIsland[];
};

/** A segment (§2.4) or commit file (§2.2): immutable, and rendered as the
 *  catch-up renders its patches (VC-D, 2026-10-08). */
export type OpsFile = { patches?: unknown[]; ops?: unknown[] };

/** An unversioned row (§5): no `_island`; `_owner` on an owned one. */
export type UnversionedRow = { id: string; _label?: string; _v?: number; _owner?: string; [field: string]: unknown };

export class OpenFailed extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = "OpenFailed";
  }
}

async function json(f: typeof fetch, url: string, init?: RequestInit): Promise<any> {
  const r = await f(url, { credentials: "include", ...init });
  if (!r.ok) throw new OpenFailed(`${init?.method ?? "GET"} failed (${r.status})`, r.status);
  return r.json();
}

export function postOpen(f: typeof fetch, base: string, req: OpenRequest, headers: Record<string, string> = {}): Promise<OpenAnswer> {
  return json(f, `${base}/versioning/open`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(req),
  });
}

export function getOpsFile(f: typeof fetch, base: string, path: string): Promise<OpsFile> {
  return json(f, `${base}${path}`);
}

/** A commit's op file. `fs`: the field set the file is rendered under —
 *  the file is immutable, so it is part of its URL; one under a field set
 *  the server no longer serves answers 410 `field_set_changed` (VC-D). */
export function commitFilePath(island: string, commit: string, fs?: string): string {
  return `/versioning/ops/${island}/commit/${commit}.json${fs ? `?fs=${encodeURIComponent(fs)}` : ''}`;
}

export async function postUnversioned(f: typeof fetch, base: string, island: string, ids: string[]): Promise<UnversionedRow[]> {
  if (!ids.length) return [];
  const body = await json(f, `${base}/versioning/unversioned`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ island, ids }),
  });
  return body?.rows ?? [];
}

/** The genesis of a never-committed island runs in the background: re-open
 *  after `retry_after_ms`, for at most this long in all. */
export const GENESIS_BUDGET_MS = 45_000;

export function genesisRetryMs(r: OpenRefusal): number | null {
  const d = r.detail;
  if (r.code !== 409 || !d || typeof d !== "object" || d.code !== "genesis_pending") return null;
  return typeof d.retry_after_ms === "number" ? d.retry_after_ms : 2_000;
}

/** A refused island in words, never a bare code (Tom: a refusal says
 *  why): the server's own words when it sends them ("This is in another
 *  organisation (X). Switch to it to open it."), else what the refusal
 *  means. A 404 always reads as not available here: its text is the
 *  server's ("Island not found"), and a stranger learns nothing. */
export function refusalText(r: OpenRefusal): string {
  const d = r.detail;
  if (r.code === 404 && (!d || typeof d === "string")) return NOT_AVAILABLE;
  if (typeof d === "string" && d) return d;
  if (d && typeof d === "object") {
    // the refusal's own words: `reason` (the one refusal shape) or `message`
    const said = typeof d.reason === "string" && d.reason ? d.reason : typeof d.message === "string" && d.message ? d.message : null;
    if (said) return said;
    if (d.code === "other_organisation") {
      return d.organisation?.name
        ? `This is in another organisation (${d.organisation.name}). Switch to it to open it.`
        : "This is in another of your organisations. Switch to it to open it.";
    }
    if (d.code === "genesis_pending") return "This is still being prepared. Try again in a moment.";
  }
  if (r.code === 404 || r.code === 403) return NOT_AVAILABLE;
  return "This could not be opened.";
}

export const NOT_AVAILABLE = "This isn't available to you here.";

/** A refusal's detail, kept on the subscription (a switch needs where). */
export function refusalDetail(r: OpenRefusal): { code?: string; message?: string; organisation?: { id: string; name?: string } } | undefined {
  const d = r.detail;
  if (!d || typeof d !== "object") return undefined;
  return {
    ...(typeof d.code === "string" ? { code: d.code } : {}),
    ...(typeof (d.reason ?? d.message) === "string" ? { message: (d.reason ?? d.message) as string } : {}),
    ...(d.organisation && typeof d.organisation.id === "string" ? { organisation: { id: d.organisation.id, ...(d.organisation.name ? { name: d.organisation.name } : {}) } } : {}),
  };
}
